"use client";

import { c as _c } from "react/compiler-runtime";
import type { KeyboardEvent } from "react";
import type { LoginPhoneState } from "@/lib/auth/login-phone.service";
import { Button } from "@/components/ui/Button";
import { InfoCallout } from "@/components/ui/InfoCallout";
import { PhoneInput } from "@/components/ui/PhoneInput";
import { OtpCodePanel } from "@/components/profile/OtpCodePanel";
import { loginPhoneCopy as copy } from "@/components/profile/login-phone-copy";
import { useLoginPhoneChange } from "@/components/profile/useLoginPhoneChange";
interface ProfilePhoneFieldProps {
  /** The login phone as `GET /api/user/profile` reports it (`login_phone`). */
  state: LoginPhoneState;
  /** The callback number (`users.phone`), shown separately when it differs. */
  callbackPhone: string | null;
  onChanged: (next: LoginPhoneState) => void;
}
const SUPPORT_EMAIL = "info@oekopunktemarkt.de";
function digits(phone: string | null | undefined): string {
  return (phone ?? "").replace(/\D/g, "");
}

/**
 * The login phone in the profile (ARCH-055). Adding or changing it is a login
 * change, so it runs behind a step-up: a code to the old phone (or, to add a
 * first phone, to the login e-mail), then a code to the new number. The
 * callback number the funnels collect is a separate thing and shown as such.
 *
 * Lives inside the profile `<form>`: every button is `type="button"`, and
 * Enter advances THIS flow instead of submitting the profile.
 */
export function ProfilePhoneField(t0) {
  const $ = _c(44);
  const {
    state,
    callbackPhone,
    onChanged
  } = t0;
  let t1;
  if ($[0] !== onChanged || $[1] !== state) {
    t1 = {
      state,
      onChanged
    };
    $[0] = onChanged;
    $[1] = state;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const flow = useLoginPhoneChange(t1);
  let t2;
  if ($[3] !== callbackPhone || $[4] !== state) {
    t2 = Boolean(callbackPhone) && digits(callbackPhone) !== digits(state.loginPhone);
    $[3] = callbackPhone;
    $[4] = state;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const callbackDiffers = t2;
  let t3;
  if ($[6] !== flow) {
    t3 = event => {
      if (event.key !== "Enter") {
        return;
      }
      event.preventDefault();
      if (flow.step === "editing") {
        flow.submitNumber();
      }
      if (flow.step === "step_up") {
        flow.stepUpChallenge.verify();
      }
      if (flow.step === "new_code") {
        flow.newCodeChallenge.verify();
      }
    };
    $[6] = flow;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleKeyDown = t3;
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <p className="text-body-sm font-medium text-foreground">{copy.label}</p>;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== flow.result || $[10] !== flow.start || $[11] !== flow.step || $[12] !== state) {
    t5 = (flow.step === "idle" || flow.step === "done") && <>{state.loginPhone ? <><div className="flex items-center justify-between gap-3 rounded-lg border border-outline-variant/40 px-3 py-2"><span className="text-body text-foreground">{state.loginPhone}</span><Button type="button" size="sm" variant="ghost" onClick={flow.start}>{copy.change}</Button></div><p className="text-body-sm text-foreground-muted">{copy.hasLogin}</p></> : <><p className="text-body-sm text-foreground-muted">{copy.noLogin}</p>{state.stepUpFactor ? <Button type="button" size="sm" variant="secondary" onClick={flow.start}>{copy.add}</Button> : <p className="text-body-sm text-foreground-muted">{copy.noFactor(SUPPORT_EMAIL)}</p>}</>}{flow.step === "done" && flow.result && <InfoCallout variant="success">{flow.result.change === "added" ? copy.doneAdded(flow.result.ending) : flow.result.change === "unchanged" ? copy.doneUnchanged(flow.result.ending) : copy.doneReplaced(flow.result.ending)}{state.realEmailMasked ? copy.mailOnTheWay : ""}</InfoCallout>}</>;
    $[9] = flow.result;
    $[10] = flow.start;
    $[11] = flow.step;
    $[12] = state;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== flow.cancel || $[15] !== flow.newPhone || $[16] !== flow.setNewPhone || $[17] !== flow.step || $[18] !== flow.submitError || $[19] !== flow.submitNumber || $[20] !== flow.submitting || $[21] !== state) {
    t6 = flow.step === "editing" && <div className="space-y-2"><PhoneInput id="login-phone" name="login-phone" value={flow.newPhone} onChange={flow.setNewPhone} autoFocus={true} describedById="login-phone-hint" /><p id="login-phone-hint" className="text-body-sm text-foreground-muted">{copy.mobileHint}</p>{state.loginPhone && <InfoCallout variant="warning">{state.phoneOnly ? copy.phoneOnlyWarning : copy.replaceWarning(state.loginPhoneEnding ?? "")}</InfoCallout>}{flow.submitError && <p role="alert" className="text-body-sm text-error">{flow.submitError}</p>}<div className="flex flex-wrap gap-2"><Button type="button" size="sm" onClick={flow.submitNumber} loading={flow.submitting} disabled={!digits(flow.newPhone) || flow.submitting}>{copy.sendCode}</Button><Button type="button" size="sm" variant="ghost" onClick={flow.cancel}>{copy.cancel}</Button></div></div>;
    $[14] = flow.cancel;
    $[15] = flow.newPhone;
    $[16] = flow.setNewPhone;
    $[17] = flow.step;
    $[18] = flow.submitError;
    $[19] = flow.submitNumber;
    $[20] = flow.submitting;
    $[21] = state;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== flow.cancel || $[24] !== flow.step || $[25] !== flow.stepUp || $[26] !== flow.stepUpChallenge) {
    t7 = flow.step === "step_up" && flow.stepUp && <OtpCodePanel intro={flow.stepUp.factor === "phone" ? copy.stepUpPhone(flow.stepUp.target) : copy.stepUpEmail(flow.stepUp.target)} challenge={flow.stepUpChallenge} resendLabel={copy.resend} onResend={flow.stepUpChallenge.send} onCancel={flow.cancel} footnote={flow.stepUp.factor === "phone" ? copy.lostPhone(SUPPORT_EMAIL) : undefined} />;
    $[23] = flow.cancel;
    $[24] = flow.step;
    $[25] = flow.stepUp;
    $[26] = flow.stepUpChallenge;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== flow.cancel || $[29] !== flow.newCodeChallenge || $[30] !== flow.newTarget || $[31] !== flow.restart || $[32] !== flow.step) {
    t8 = flow.step === "new_code" && <OtpCodePanel intro={copy.newCode(flow.newTarget)} challenge={flow.newCodeChallenge} resendLabel={copy.restart} onResend={flow.restart} onCancel={flow.cancel} />;
    $[28] = flow.cancel;
    $[29] = flow.newCodeChallenge;
    $[30] = flow.newTarget;
    $[31] = flow.restart;
    $[32] = flow.step;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  let t9;
  if ($[34] !== callbackDiffers || $[35] !== callbackPhone) {
    t9 = callbackDiffers && callbackPhone && <p className="text-body-sm text-foreground-muted">{copy.callback(callbackPhone)}</p>;
    $[34] = callbackDiffers;
    $[35] = callbackPhone;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  let t10;
  if ($[37] !== handleKeyDown || $[38] !== t5 || $[39] !== t6 || $[40] !== t7 || $[41] !== t8 || $[42] !== t9) {
    t10 = <div className="space-y-2" onKeyDown={handleKeyDown} data-field="login-phone">{t4}{t5}{t6}{t7}{t8}{t9}</div>;
    $[37] = handleKeyDown;
    $[38] = t5;
    $[39] = t6;
    $[40] = t7;
    $[41] = t8;
    $[42] = t9;
    $[43] = t10;
  } else {
    t10 = $[43];
  }
  return t10;
}